import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { Box, Button, Column, DatePicker, Popover, Typography } from 'seagull';
import { useLocaleKeys } from '~/i18n';
import { FilterChip } from '../FilterChip/FilterChip';
import { isEqMode, toDateOrNull, toIsoDate, valueLabelFor, valuePrefixFor } from './DateRangeChip.utils';
export const DateRangeChip = (param)=>{
    let { testId, predicate, onChange, fieldLabel, biNames, popoverTestIds, tone = 'default' } = param;
    const { t } = useLocaleKeys('pages.draftsDashboard.aiSearch');
    const [anchorEl, setAnchorEl] = useState(null);
    const eqMode = isEqMode(predicate);
    const isExclude = predicate.neq != null;
    const [gte, setGte] = useState(toDateOrNull(predicate.gte));
    const [lte, setLte] = useState(toDateOrNull(predicate.lte));
    const [validationError, setValidationError] = useState(null);
    const openPopover = (event)=>{
        setGte(toDateOrNull(predicate.gte));
        setLte(toDateOrNull(predicate.lte));
        setValidationError(null);
        setAnchorEl(event.currentTarget);
    };
    const closePopover = ()=>setAnchorEl(null);
    const toggleOperator = ()=>{
        onChange(isExclude ? {
            eq: predicate.neq
        } : {
            neq: predicate.eq
        });
    };
    const changeGte = (date)=>{
        setGte(date);
        setValidationError(null);
    };
    const changeLte = (date)=>{
        setLte(date);
        setValidationError(null);
    };
    const apply = ()=>{
        if (gte != null && lte != null && gte > lte) {
            setValidationError(t.dateRangeValidationError());
            return;
        }
        const next = {};
        if (gte != null) next.gte = toIsoDate(gte);
        if (lte != null) next.lte = toIsoDate(lte);
        onChange(next.gte == null && next.lte == null ? undefined : next);
        closePopover();
    };
    const gteActions = useMemo(()=>[
            {
                label: t.clearButton(),
                onClick: ()=>changeGte(null),
                bi: {
                    name: 'clear_gte'
                }
            }
        ], []);
    const lteActions = useMemo(()=>[
            {
                label: t.clearButton(),
                onClick: ()=>changeLte(null),
                bi: {
                    name: 'clear_lte'
                }
            }
        ], []);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(FilterChip, {
                testId: testId,
                fieldLabel: fieldLabel,
                operator: eqMode ? {
                    label: isExclude ? t.operatorIsNot() : t.operatorIs(),
                    onToggle: toggleOperator,
                    biName: biNames.toggleOperator
                } : undefined,
                valuePrefix: valuePrefixFor(predicate),
                valueLabel: valueLabelFor(predicate, fieldLabel),
                onValueClick: eqMode ? undefined : openPopover,
                onRemove: ()=>onChange(undefined),
                removeBiName: biNames.remove,
                tone: tone
            }),
            /*#__PURE__*/ _jsx(Popover, {
                testId: popoverTestIds.EditPopover,
                open: anchorEl != null,
                anchorEl: anchorEl,
                onClose: closePopover,
                children: /*#__PURE__*/ _jsx(Box, {
                    padding: 12,
                    children: /*#__PURE__*/ _jsxs(Column, {
                        gap: 8,
                        children: [
                            /*#__PURE__*/ _jsx(DatePicker, {
                                testId: popoverTestIds.GteField,
                                bi: {
                                    name: biNames.editGte
                                },
                                label: t.fromLabel(),
                                value: gte,
                                onChange: changeGte,
                                actions: gteActions,
                                disableErrors: true
                            }),
                            /*#__PURE__*/ _jsx(DatePicker, {
                                testId: popoverTestIds.LteField,
                                bi: {
                                    name: biNames.editLte
                                },
                                label: t.toLabel(),
                                value: lte,
                                onChange: changeLte,
                                actions: lteActions,
                                disableErrors: true
                            }),
                            validationError != null && /*#__PURE__*/ _jsx(Typography, {
                                testId: popoverTestIds.ValidationError,
                                skin: "error",
                                variant: "paragraph2",
                                children: validationError
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                testId: popoverTestIds.ApplyButton,
                                bi: {
                                    name: biNames.apply
                                },
                                onClick: apply,
                                children: t.applyButton()
                            })
                        ]
                    })
                })
            })
        ]
    });
};
